import { useCallback } from "react";
import { useAuth } from "./useAuth";

/**
 * A fetch wrapper bound to the current access token.
 *
 * Centralizing the Authorization header here means no component attaches a
 * token by hand (conventions/frontend-stack.md: API Request Authorization).
 * Implemented as a hook rather than a module-level singleton so the token is
 * read from context on each call and never cached outside React state.
 */
export function useApi(): (path: string, init?: RequestInit) => Promise<Response> {
  const { accessToken } = useAuth();

  return useCallback(
    async (path: string, init: RequestInit = {}) => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      if (!baseUrl) {
        throw new Error("VITE_API_BASE_URL is not configured for this environment.");
      }
      if (!accessToken) {
        throw new Error("No access token available — the user is not authenticated.");
      }

      const headers = new Headers(init.headers);
      headers.set("Authorization", `Bearer ${accessToken}`);
      if (!headers.has("Content-Type") && init.body) {
        headers.set("Content-Type", "application/json");
      }

      return fetch(`${baseUrl.replace(/\/$/, "")}${path}`, { ...init, headers });
    },
    [accessToken],
  );
}
